const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:4000";

interface ApiError {
  error: string;
  details?: unknown;
}

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem("auth_token", token);
    } else {
      localStorage.removeItem("auth_token");
    }
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem("auth_token");
    }
    return this.token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE}${endpoint}`;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...((options.headers as Record<string, string>) ?? {}),
    };

    const token = this.getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error((data as ApiError).error ?? "Request failed");
    }

    return data as T;
  }

  // Auth endpoints
  async register(email: string, username: string, password: string) {
    const data = await this.request<{
      token: string;
      user: { id: string; email: string; username: string; isGuest: boolean };
    }>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, username, password }),
    });
    this.setToken(data.token);
    return data;
  }

  async login(email: string, password: string) {
    const data = await this.request<{
      token: string;
      user: { id: string; email: string; username: string; isGuest: boolean };
    }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.token);
    return data;
  }

  async createGuest(username?: string) {
    const data = await this.request<{
      token: string;
      user: { id: string; email: string | null; username: string; isGuest: boolean };
    }>("/api/auth/guest", {
      method: "POST",
      body: JSON.stringify({ username }),
    });
    this.setToken(data.token);
    return data;
  }

  async getMe() {
    return this.request<{
      id: string;
      email: string | null;
      username: string;
      isGuest: boolean;
    }>("/api/auth/me");
  }

  async refreshToken() {
    const data = await this.request<{
      token: string;
      user: { id: string; email: string | null; username: string; isGuest: boolean };
    }>("/api/auth/refresh", { method: "POST" });
    this.setToken(data.token);
    return data;
  }

  logout() {
    this.setToken(null);
  }

  // History endpoints
  async getGameHistory() {
    return this.request<{ games: GameHistoryRecord[] }>("/api/history");
  }

  async saveGame(game: SaveGamePayload) {
    return this.request<GameHistoryRecord>("/api/history", {
      method: "POST",
      body: JSON.stringify(game),
    });
  }

  async getStats() {
    return this.request<GameStats>("/api/history/stats/summary");
  }

  async syncGames(games: SaveGamePayload[], lastSyncTime: number) {
    return this.request<{ games: GameHistoryRecord[]; conflicts: string[] }>(
      "/api/history/sync",
      {
        method: "POST",
        body: JSON.stringify({ games, lastSyncTime }),
      }
    );
  }
}

export interface GameHistoryRecord {
  id: string;
  odId: string;
  templateId: string;
  status: "in_progress" | "completed" | "abandoned";
  finalScore: number | null;
  startedAt: number;
  completedAt: number | null;
  turns: unknown[];
  sheetSnapshot: Record<string, unknown>;
  syncStatus: "local" | "synced" | "pending" | "conflict";
  version: number;
  updatedAt: number;
}

export interface SaveGamePayload {
  id: string;
  templateId: string;
  status: "in_progress" | "completed" | "abandoned";
  finalScore: number | null;
  startedAt: number;
  completedAt: number | null;
  turns: unknown[];
  sheetSnapshot: Record<string, unknown>;
  version: number;
}

export interface GameStats {
  totalGames: number;
  completedGames: number;
  averageScore: number;
  highScore: number;
  lowScore: number;
  categoryStats: Record<
    string,
    {
      timesScored: number;
      totalPoints: number;
      averagePoints: number;
      bestScore: number;
    }
  >;
  templateStats: Record<
    string,
    {
      gamesPlayed: number;
      averageScore: number;
      highScore: number;
    }
  >;
}

export const apiClient = new ApiClient();
